import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { can } from "../lib/roles";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { ListRow, StatusLabel } from "../components/ListTable";
import { Results, ItemCard } from "../components/Paged";
import { Icon } from "../app/Icon";
import { inputClass, labelClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import { adminQueue, adminReview, type QueueItem } from "../lib/verification";

const TABS: { key: string; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "needs_info", label: "Needs info" },
  { key: "verified", label: "Verified" },
  { key: "rejected", label: "Rejected" },
];

function ago(at: string): string {
  const s = Math.max(0, (Date.now() - +new Date(at)) / 1000);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function AdminVerification() {
  const { roles } = useAuth();
  const isAdmin = can(roles, "admin_verification");
  const [tab, setTab] = useState("pending");
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [selected, setSelected] = useState<QueueItem | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function refresh(status = tab) {
    if (!isAdmin) return;
    const r = await adminQueue(status);
    setQueue(r.queue);
    setCounts(r.counts);
  }
  useEffect(() => {
    setSelected(null);
    void refresh(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);

  async function decide(decision: string) {
    if (!selected) return;
    if (decision !== "verified" && !reason.trim()) {
      setNote("A reason is required to request info or reject.");
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      await adminReview(selected.id, decision, reason.trim() || undefined);
      setSelected(null);
      setReason("");
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Verification queue" subtitle="Admin access required." />
        <p className="font-mono text-sm text-danger">Admins only.</p>
      </div>
    );
  }

  // --- Review detail (#47) ---
  if (selected) {
    const claimed = (selected.claimed ?? {}) as Record<string, unknown>;
    const claimedRows = Object.entries(claimed).filter(([, v]) => v != null && String(v).length > 0);
    return (
      <div className="grid gap-6">
        <button type="button" onClick={() => setSelected(null)} className="flex w-max items-center gap-1 font-mono text-sm font-bold text-accent-strong">
          <Icon name="chevron" className="h-4 w-4 rotate-90" /> Queue
        </button>
        <PageHeader title={`${selected.display_name ?? "Member"} · ${labelize(selected.profile_type)}`} subtitle={`Submitted ${ago(selected.created_at)} · ${selected.account_email ?? ""}`} />

        <div className="grid gap-6 lg:grid-cols-2">
          <Panel title={`Document · ${labelize(selected.doc_type)}`}>
            {selected.document_url ? (
              <>
                {/* SECURITY: sandbox the preview so a submitted document that is secretly
                    HTML/SVG cannot run scripts in a reviewer's browser. `sandbox` with no
                    tokens blocks scripts, forms, popups, and same-origin access. */}
                <div className="overflow-hidden rounded-lg border border-line bg-surface-2">
                  <iframe title="Submitted document" src={selected.document_url} sandbox="" referrerPolicy="no-referrer" className="h-80 w-full" />
                </div>
                <a href={selected.document_url} download target="_blank" rel="noreferrer noopener" className="font-mono text-xs font-bold text-accent-strong">Download document ↓</a>
              </>
            ) : (
              <p className="font-mono text-sm text-muted">No document was attached.</p>
            )}
          </Panel>

          <div className="grid content-start gap-6">
            <Panel title="Claimed">
              {claimedRows.length === 0 ? (
                <p className="text-sm text-muted">No profile detail on file.</p>
              ) : (
                <ul className="grid gap-1.5 font-mono text-sm">
                  {claimedRows.map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-4"><span className="text-muted">{labelize(k)}</span><span className="font-bold text-ink">{Array.isArray(v) ? v.join(", ") : String(v)}</span></li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Decision">
              {selected.status !== "pending" && <p className="mb-2 font-mono text-xs">Current: <StatusLabel status={selected.status} /></p>}
              <label className={labelClass}>
                Reason / note (required to request info or reject)
                <textarea className={inputClass} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Add a note or request more information…" />
              </label>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => decide("verified")} disabled={busy}>Verify</Button>
                <Button variant="ghost" onClick={() => decide("needs_info")} disabled={busy}>Needs info</Button>
                <Button variant="ghost" onClick={() => decide("rejected")} disabled={busy}>Reject</Button>
              </div>
              {note && <p className="font-mono text-xs text-danger">{note}</p>}
            </Panel>
          </div>
        </div>
      </div>
    );
  }

  // --- Tabbed queue (#48) ---
  return (
    <div className="grid gap-6">
      <PageHeader title="Verification queue" subtitle="Review submitted driver and carrier credentials." />

      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === t.key ? "border-accent text-accent-strong" : "border-transparent text-muted hover:text-ink"}`}
          >
            {t.label} ({counts[t.key] ?? 0})
          </button>
        ))}
      </div>

      <Results
        items={queue}
        perPage={12}
        defaultView="list"
        listColumns={["Profile / account", "Type", "Submitted"]}
        gridClass="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        empty={<p className="font-mono text-sm text-muted">Nothing here.</p>}
        renderRow={(item) => (
          <ListRow key={item.id} onClick={() => setSelected(item)} title={item.display_name ?? "—"} meta={item.account_email ?? ""} status={labelize(item.profile_type)} right={<span className="font-mono text-xs text-muted">{labelize(item.doc_type)} · {ago(item.created_at)}</span>} />
        )}
        renderCard={(item) => (
          <ItemCard key={item.id} onClick={() => setSelected(item)} title={item.display_name ?? "—"} meta={item.account_email ?? ""} status={labelize(item.profile_type)} right={`${labelize(item.doc_type)} · ${ago(item.created_at)}`} />
        )}
      />
    </div>
  );
}
